import React, { Component } from 'react'
import { Table, Icon, Tooltip } from 'antd'
import { tranzila } from '@services/api'

interface InvoiceItem {
    id: number
    number: string
    created_at: string
    total_charge_amount: number
    retrieval_key: string
}

interface Props {
    userId: number | string
    roles: string[]
    userIsCollective?: string
}

interface State {
    data: InvoiceItem[]
    loading: boolean
    error: string | null
}

export default class StoTab extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            data: [],
            loading: false,
            error: null
        }
    }

    componentDidMount() {
        const { userId, userIsCollective } = this.props
        this.setState({ loading: true }, async () => {
            tranzila
                .getALLStoHistoryData({ userId, userIsCollective })
                .then(({ status, data }) => {
                    console.log('Sto fetched:', data)
                    this.setState({ loading: false, data })
                })
                .catch(err => {
                    console.log(err && err.response && err.response.data)
                    this.setState({ loading: false })
                })
        })
    }

    formatDate(dateStr: string, timeStr?: boolean) {
        const date = new Date(dateStr)
        if (!timeStr) return date.toLocaleDateString('he-IL')
        return (
            date.toLocaleDateString('he-IL') +
            ' - ' +
            date.toLocaleTimeString('he-IL', {
                hour: '2-digit',
                minute: '2-digit'
            })
        )
    }

    render() {
        const { data, loading, error } = this.state
        const { roles } = this.props
        const hasAcceptedRole = roles.includes('admin') || roles.includes('money')
        const statuses = {
            active: { title: 'פעיל', color: '#4caf50' },
            inactive: { title: 'לא פעיל', color: '#f44336' }
        }
        const terminals = {
            mediwho1tok: 'אשראי',
            mediwhobk: 'הו״ק',
            mediwhotok: 'אשראי (מסוף ישן)',
            // ISHPUZPLUS-PENDING: terminal not yet in Tranzila
            // ishpuzplus: 'אשראי (מסוף אשפוז)'
        }
        const columns = [
            {
                title: 'מזהה',
                key: 'sto_id',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.sto_id}</div>
                }
            },
            {
                title: 'אמצעי תשלום',
                key: 'terminal_name',
                align: 'center',
                render: (_, record) => {
                    const isBank = record.terminal_name === 'mediwhobk'
                    const paymentInfo = (isBank ? record.msv : record.card) || {}
                    const itemType = isBank ? 'bank' : 'credit-card'
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'end',
                                justifyContent: 'center',
                                gap: 8
                            }}
                        >
                            <Tooltip
                                placement="topRight"
                                title={
                                    itemType === 'bank' ? (
                                        <div>
                                            <div>קוד בנק: {paymentInfo.bank_code}</div>
                                            <div>מספר סניף: {paymentInfo.branch_code}</div>
                                            <div>מספר חשבון: {paymentInfo.account_number}</div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div>
                                                תוקף כרטיס: {paymentInfo.expire_month + '/' + paymentInfo.expire_year}
                                            </div>
                                            <div>
                                                ספרות אחרונות:{' '}
                                                {paymentInfo.token ? paymentInfo.token.trim().slice(-4) : ''}
                                            </div>
                                        </div>
                                    )
                                }
                            >
                                <Icon
                                    type={itemType}
                                    theme="twoTone"
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                />
                            </Tooltip>
                            <div>{terminals[record.terminal_name]}</div>
                        </div>
                    )
                }
            },
            {
                title: 'תאריך חיוב ראשון',
                key: 'first_charge_date',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.first_charge_date ? this.formatDate(record.first_charge_date) : '-'}</div>
                }
            },
            {
                title: 'תאריך חיוב אחרון',
                key: 'last_charge_date',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.last_charge_date ? this.formatDate(record.last_charge_date, true) : '-'}</div>
                }
            },
            {
                title: 'תאריך חיוב הבא',
                key: 'next_charge_date',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.next_charge_date ? this.formatDate(record.next_charge_date) : '-'}</div>
                }
            },
            {
                title: 'תאריך יצירה',
                key: 'create_date',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.create_date ? this.formatDate(record.create_date, true) : '-'}</div>
                }
            },
            {
                title: 'סטטוס',
                key: 'sto_status',
                align: 'center',
                render: (_, record) => {
                    const statusEntry = statuses[record.sto_status] || { color: '#999', title: '-' }
                    return <div style={{ color: statusEntry.color }}>{statusEntry.title}</div>
                }
            },
            {
                title: 'סכום',
                key: 'charge_amount',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.charge_amount ? String(record.charge_amount).split('.')[0] + '₪' : '-'}</div>
                }
            },
            {
                title: 'שם מנוי',
                key: 'client_info',
                align: 'center',
                render: (_, record) => {
                    return <div style={{ direction: 'ltr' }}>{record.user_sub_name}</div>
                }
            }
        ]
        if (hasAcceptedRole) {
            columns.push({
                title: 'מספר מנוי',
                key: 'user_sub_id',
                align: 'center',
                render: (_, record) => {
                    return <div>{record.user_sub_id || '-'}</div>
                }
            })
        }

        return (
            <div style={{ direction: 'rtl' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <Icon type="loading" style={{ fontSize: 24 }} spin />
                    </div>
                ) : error ? (
                    <div style={{ color: 'red', textAlign: 'center', padding: 20 }}>{error}</div>
                ) : (
                    <Table
                        rowKey="id"
                        dataSource={data}
                        columns={columns}
                        locale={{ emptyText: 'לא נמצאו פרטים' }}
                        pagination={{ pageSize: 10 }}
                    />
                )}
            </div>
        )
    }
}
